import { useState, useEffect, useMemo } from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import { create } from "jss";
import socketIO from "socket.io-client";
import rtl from "jss-rtl";
import { StylesProvider, jssPreset } from "@mui/styles";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import SuiBox from "components/SuiBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import Error404 from "layouts/authentication/error/404";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import routes from "routes";
import { useSoftUIController } from "context";

import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import ProtectedRoute from "components/ProtectedRoute/index";

import axios from "./api/index";

const socket = socketIO.connect("http://localhost:8080");

function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  if (process.env.NODE_ENV === "production") {
    console.log = function () {};
  }
  // JSS presets for the rtl
  const jss = create({
    plugins: [...jssPreset().plugins, rtl()],
  });

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      prepend: true,
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      dispatch({ type: "MINI_SIDENAV", value: false });
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      dispatch({ type: "MINI_SIDENAV", value: true });
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => {
    dispatch({ type: "OPEN_CONFIGURATOR", value: !openConfigurator });
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const localUser = localStorage.getItem("user");
  const user = typeof localUser === "string" ? JSON.parse(localUser) : localUser;
  const accessToken = localStorage.getItem("accessToken");
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        const Component = route.component;
        if (route.protected || route.route.protected) {
          const eligible = route?.roles && route?.roles.find((r) => r === user?.role_code);
          if (!eligible) {
            return (
              <Route
                exact
                path="/error/404"
                render={() => <Error404 message="hi" />}
                key={route.key}
              />
            );
          }
          return (
            <ProtectedRoute exact path={route.route} component={route.component} key={route.key} />
          );
        }

        return (
          <Route
            exact
            path={route.route}
            render={() => <Component message="hi" socket={socket} />}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <SuiBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      backgroundColor="white"
      boxShadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      customClass="cursor-pointer"
      onClick={handleConfiguratorOpen}
    >
      <Icon className=" text-dark" fontSize="default">
        person
      </Icon>
    </SuiBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <StylesProvider jss={jss}>
        <ThemeProvider theme={themeRTL}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {configsButton}
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Switch>
            {getRoutes(routes)}
            {!user || !user.token || user.token === "" ? (
              <Redirect from="*" to="/authentication/sign-in" />
            ) : (
              <>
                {user?.role_code === "marketing" ? (
                  <Redirect from="*" to="/memberlist" />
                ) : (
                  <Redirect from="*" to="/dashboards" />
                )}
              </>
            )}

            {/* <Redirect from="*" to="/dashboards" /> */}
          </Switch>
        </ThemeProvider>
      </StylesProvider>
    </CacheProvider>
  ) : (
    // </CacheProvider>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Switch>
          {getRoutes(routes)}
          {!user || !user.token || user.token === "" ? (
            <Redirect from="*" to="/authentication/sign-in" />
          ) : (
            <>
              {user?.role_code === "marketing" ? (
                <Redirect from="*" to="/memberlist" />
              ) : (
                <Redirect from="*" to="/dashboards" />
              )}
            </>
          )}

          {/* <Redirect from="*" to="/dashboards" /> */}
        </Switch>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

// App.defaultProps = {
//   channel: undefined,
// };

// // Typechecking props for the WheatherCard
// App.propTypes = {
//   channel: PropTypes.shape,
// };

export default App;
